import React, { createContext, useContext, useReducer, useMemo } from 'react'

// Action types para el reducer del game context
export const GAME_CONTEXT_ACTIONS = {
  SET_PERFORMANCE_MODE: 'SET_PERFORMANCE_MODE',
  SET_ACCESSIBILITY_MODE: 'SET_ACCESSIBILITY_MODE',
  SET_THEME: 'SET_THEME',
  SET_SOUND_ENABLED: 'SET_SOUND_ENABLED',
  SET_DEBUG_MODE: 'SET_DEBUG_MODE',
  TOGGLE_FULLSCREEN: 'TOGGLE_FULLSCREEN',
  SET_LANGUAGE: 'SET_LANGUAGE',
  UPDATE_VIEWPORT: 'UPDATE_VIEWPORT'
}

// Estado inicial del contexto
const initialState = {
  performanceMode: false,
  accessibilityMode: false,
  theme: 'default',
  soundEnabled: true,
  debugMode: process.env.NODE_ENV === 'development',
  isFullscreen: false,
  language: 'en',
  viewport: {
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768
  }
}

// Reducer para manejar las acciones del contexto
function gameContextReducer(state, action) {
  switch (action.type) {
    case GAME_CONTEXT_ACTIONS.SET_PERFORMANCE_MODE:
      return { ...state, performanceMode: action.payload }
    
    case GAME_CONTEXT_ACTIONS.SET_ACCESSIBILITY_MODE:
      return { ...state, accessibilityMode: action.payload }
    
    case GAME_CONTEXT_ACTIONS.SET_THEME:
      return { ...state, theme: action.payload }
    
    case GAME_CONTEXT_ACTIONS.SET_SOUND_ENABLED:
      return { ...state, soundEnabled: action.payload }
    
    case GAME_CONTEXT_ACTIONS.SET_DEBUG_MODE:
      return { ...state, debugMode: action.payload }
    
    case GAME_CONTEXT_ACTIONS.TOGGLE_FULLSCREEN:
      return { ...state, isFullscreen: !state.isFullscreen }
    
    case GAME_CONTEXT_ACTIONS.SET_LANGUAGE:
      return { ...state, language: action.payload }
    
    case GAME_CONTEXT_ACTIONS.UPDATE_VIEWPORT:
      return { 
        ...state, 
        viewport: {
          ...action.payload,
          isMobile: action.payload.width < 768
        }
      }
    
    default:
      return state
  }
}

// Crear el contexto
const GameContext = createContext(null)

// Hook personalizado para usar el contexto del juego
export const useGameContext = () => {
  const context = useContext(GameContext)
  if (!context) {
    throw new Error('useGameContext debe ser usado dentro de GameContextProvider')
  }
  return context
}

// Provider del contexto del juego
export const GameContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(gameContextReducer, initialState)

  // Action creators memoizados
  const actions = useMemo(() => ({
    setPerformanceMode: (enabled) => 
      dispatch({ type: GAME_CONTEXT_ACTIONS.SET_PERFORMANCE_MODE, payload: enabled }),
    
    setAccessibilityMode: (enabled) => 
      dispatch({ type: GAME_CONTEXT_ACTIONS.SET_ACCESSIBILITY_MODE, payload: enabled }),
    
    setTheme: (theme) => 
      dispatch({ type: GAME_CONTEXT_ACTIONS.SET_THEME, payload: theme }),
    
    setSoundEnabled: (enabled) => 
      dispatch({ type: GAME_CONTEXT_ACTIONS.SET_SOUND_ENABLED, payload: enabled }),
    
    setDebugMode: (enabled) => 
      dispatch({ type: GAME_CONTEXT_ACTIONS.SET_DEBUG_MODE, payload: enabled }),
    
    toggleFullscreen: () => 
      dispatch({ type: GAME_CONTEXT_ACTIONS.TOGGLE_FULLSCREEN }),
    
    setLanguage: (language) => 
      dispatch({ type: GAME_CONTEXT_ACTIONS.SET_LANGUAGE, payload: language }),
    
    updateViewport: (viewport) => 
      dispatch({ type: GAME_CONTEXT_ACTIONS.UPDATE_VIEWPORT, payload: viewport })
  }), [])

  // Valor del contexto memoizado
  const contextValue = useMemo(() => ({
    ...state,
    actions
  }), [state, actions])

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  )
}

export default GameContext